// ViewDepartment.js
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Breadcrumb, Spinner, Alert, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { NavLink, useParams } from 'react-router-dom';
import Base from '../Base';
import { FaTrash, FaPlus } from 'react-icons/fa';
import AddEmployeeOffcanvas from './AddEmployeeOffcanvas';
import { getDepartmentDetails, getAssignedUsersList, removeUserFromDepartment } from './departmentHelper';
import toast from 'react-hot-toast';
import DynamicModal from '../Employees/DynamicModal';
import { truncateText, truncateTextOnly } from '../../utils';
import PaginationComponent from '../common/Pagination';
import { extractErrorMessage } from '../../constants/common';

const ViewDepartment = () => {
    const { department_id: departmentId } = useParams();
    const [departmentDetails, setDepartmentDetails] = useState({
        name: '',
        parent: null,
        manager: null,
        totalMembers: 0
    });
    const [showAddEmployee, setShowAddEmployee] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage] = useState(25);
    const [totalCount, setTotalCount] = useState(0);
    const [confirmModal, setConfirmModal] = useState({
        show: false,
        message: '',
        onConfirm: null,
        loading: false
    });

    // Function to handle pagination
    const handlePagination = (page) => {
        setCurrentPage(page);
    };

    // Function to handle employee added (refresh assigned users list)
    const handleEmployeeAdded = async () => {
        await fetchAssignedUsers();
    };

    //  Function to handle remove employee confirmation
    const handleRemoveEmployee = (member) => {
    console.log(member.name)
    const name = truncateTextOnly(member?.name, 50) // Use text-only version
    setConfirmModal({
        show: true,
        message: `Are you sure you want to remove ${name} from this department?`,
        onConfirm: () => removeEmployee(member.user_id),
        loading: false
    });
};

    // Function to remove employee from department
    const removeEmployee = async (userId) => {
        try {
            setConfirmModal(prev => ({ ...prev, loading: true }));
            const response = await removeUserFromDepartment(departmentId, userId);
            if (response.status === 'success') {
                toast.success('Employee removed from department successfully');
                await fetchAssignedUsers();
            } else {
                const errorMessage = extractErrorMessage(response);
                toast.error(errorMessage);
            }
        } catch (error) {
            console.error('Error removing employee:', error);
            const errorMessage = extractErrorMessage(error);
            toast.error(errorMessage);
        } finally {
            setConfirmModal(prev => ({ ...prev, loading: false }));
        }
    };

    // Function to fetch department details
    const fetchDepartmentDetails = async () => {
        try {
            const response = await getDepartmentDetails(departmentId);
            if (response.status === 'success') {
                const data = response.data;
                setDepartmentDetails({
                    name: data.name,
                    parent: data.parent || 'No Parent',
                    parent_name: data.parent_name || 'No Parent',
                    manager: data.manager ? data.manager.email : 'No Manager',
                    totalMembers: 0 // Will be updated when we get the members list
                });
            }
        } catch (error) {
            setError('Failed to fetch department details');
            console.error('Error fetching department details:', error);
        }
    };

    // Function to fetch assigned users list
    const fetchAssignedUsers = async () => {
        try {
            const response = await getAssignedUsersList(departmentId, currentPage, perPage);
            if (response.status === 'success') {
                const data = response.data;

                // Transform the API data to match the table structure
                const transformedMembers = data.list.map(item => ({
                    id: item.id,
                    name: item?.profile?.name, // Extract name from email
                    email: item?.profile?.username,
                    role: item?.profile?.role || 'Employee',
                    user_id: item?.profile?.id
                }));

                setMembers(transformedMembers);
                setTotalCount(data.total_count);

                // Update total members count in department details
                setDepartmentDetails(prev => ({
                    ...prev,
                    totalMembers: data.total_count
                }));
            }
        } catch (error) {
            setError('Failed to fetch assigned users');
            console.error('Error fetching assigned users:', error);
        }
    };

    // Fetch data on component mount
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                await Promise.all([
                    fetchDepartmentDetails(),
                    fetchAssignedUsers()
                ]);
            } catch (error) {
                setError('Failed to load data');
            } finally {
                setLoading(false);
            }
        };

        if (departmentId) {
            fetchData();
        }
    }, [departmentId, currentPage]);

    if (loading) {
        return (
            <Base>
                <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </Container>
            </Base>
        );
    }

    return (
        <Base>
            <Container>
                <Row>
                    <Col className="mr-auto text-center">
                        <Breadcrumb>
                            <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: '/' }}>Dashboard</Breadcrumb.Item>
                            <Breadcrumb.Item linkAs={NavLink} linkProps={{ to: '/manage-department' }}>Departments</Breadcrumb.Item>
                            <Breadcrumb.Item active>View Department</Breadcrumb.Item>
                        </Breadcrumb>
                    </Col>
                </Row>

                {error && (
                    <Row>
                        <Col>
                            <Alert variant="danger">{error}</Alert>
                        </Col>
                    </Row>
                )}

                <div className="flex justify-between items-center ">
                    <h1 className="text-[28px] font-bold text-gray-800">Department Details</h1>
                </div>

                {/* Department Details Card */}
                <Row>
                    <Col>
                        <Card className="shadow-sm border-0">
                            <Card.Body>
                                <div>
                                    <Row className="mb-1 d-flex justify-content-end align-items-center">
                                        <Col xs="auto">
                                            <Button className="d-flex align-items-center fw-semibold" variant="primary" onClick={() => setShowAddEmployee(true)}>
                                                <FaPlus className="me-2" />
                                                Add Employee
                                            </Button>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <p className="mb-1 text-muted small">Department Name</p>
                                            <p className="fw-semibold">{truncateText(departmentDetails.name,30)}</p>
                                        </Col>
                                        <Col md={6}>
                                            <p className="mb-1 text-muted small">Parent Department</p>
                                            <p className="fw-semibold">{truncateText(departmentDetails.parent_name,30)}</p>
                                        </Col>
                                        <Col md={6}>
                                            <p className="mb-1 text-muted small">Manager</p>
                                            <p className="fw-semibold">{truncateText(departmentDetails.manager,30)}</p>
                                        </Col>
                                        <Col md={6}>
                                            <p className="mb-1 text-muted small">Total Members</p>
                                            <p className="fw-semibold">{departmentDetails.totalMembers}</p>
                                        </Col>
                                    </Row>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Members Table */}
                <Row>
                    <div className="flex justify-between items-center mb-1">
                        <h1 className="text-[28px] font-bold text-gray-800">Employee Details</h1>
                    </div>
                    <Col>
                        <Table responsive className="core-ui-table">
                            <thead className="bg-lights">
                                <tr>
                                    <th className="text-left">Name</th>
                                    <th className="text-left">Email</th>
                                    <th className="text-left">Role</th>
                                    <th className="text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {members.length > 0 ? (
                                    members.map((member, idx) => (
                                        <tr key={member.id || idx}>
                                            <td className="text-left">
                                                {truncateText(member.name, 15)}
                                            </td>
                                            <td className="text-left">
                                                {truncateText(member.email, 25)}
                                            </td>
                                            <td className="text-left">
                                                {truncateText(member.role, 12)}
                                            </td>
                                            <td className="text-center">
                                                <Button
                                                    variant="link"
                                                    className="text-danger p-0"
                                                    onClick={() => handleRemoveEmployee(member)}
                                                    title="Remove Employee"
                                                >
                                                    <FaTrash />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="4" className="text-center py-4">
                                            <div className="text-muted">
                                                <p className="mb-0">No employees assigned to this department</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </Col>
                </Row>

                {/* Pagination Component */}
                {totalCount > perPage && (
                    <Row>
                        <Col md={12} className="d-flex justify-content-center">
                            <PaginationComponent
                                total_count={totalCount}
                                current_page={currentPage}
                                handleOnClick={handlePagination}
                                per_page={perPage}
                            />
                        </Col>
                    </Row>
                )}

                {/* Offcanvas moved to separate component */}
                <AddEmployeeOffcanvas
                    show={showAddEmployee}
                    onHide={() => setShowAddEmployee(false)}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    departmentId={departmentId}
                    departmentName={departmentDetails.name}
                    onEmployeeAdded={handleEmployeeAdded}
                />

                {/*  Added DynamicModal for confirmation */}
                <DynamicModal
                    show={confirmModal.show}
                    onHide={() => setConfirmModal({ ...confirmModal, show: false })}
                    title="Confirm Remove"
                    buttons={[
                        {
                            label: 'Cancel',
                            variant: 'secondary',
                            action: 'custom',
                            onClick: () => setConfirmModal({ ...confirmModal, show: false })
                        },
                        {
                            label: confirmModal.loading ? 'Removing...' : 'Remove',
                            variant: 'danger',
                            action: 'custom',
                            disabled: confirmModal.loading,
                            onClick: async () => {
                                if (confirmModal.onConfirm) {
                                    await confirmModal.onConfirm();
                                    setConfirmModal({ ...confirmModal, show: false });
                                }
                            }
                        }
                    ]}
                >
                    <p>{confirmModal.message}</p>
                </DynamicModal>
            </Container>
        </Base>
    );
};

export default ViewDepartment;